import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ImagePlus, Trash2 } from "lucide-react";
import client, { absoluteUrl } from "../api/client";
import Modal from "../Moodboard/Modal";

const FIELDS = [
  ["firm_name", "Firm name", 120],
  ["phone", "Phone", 40],
  ["email", "Email", 160],
  ["gstin", "GSTIN", 20],
];

// The letterhead on quotations and spec sheets.
export default function FirmProfileDialog({ onClose }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    client
      .get("/api/firm-profile")
      .then(({ data }) => setForm(data))
      .catch(() => toast.error("Could not load your firm details."));
  }, []);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await client.post("/api/uploads", body);
      setForm((current) => ({ ...current, logo_upload_id: data.id, logo_url: data.url }));
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not upload the logo.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await client.put("/api/firm-profile", form);
      toast.success("Firm details saved.");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save your firm details.");
      setBusy(false);
    }
  };

  return (
    <Modal title="Firm details for quotations" onClose={onClose} wide>
      {!form ? (
        <p className="ar-muted">Loading...</p>
      ) : (
        <form className="ar-form" onSubmit={save}>
          <div className="ar-logo-row">
            <div className="ar-logo-box">
              {form.logo_url ? <img src={absoluteUrl(form.logo_url) || undefined} alt="Firm logo" /> : <span>No logo</span>}
            </div>
            <div className="ar-logo-actions">
              <button type="button" className="mb-wide-button ar-fit" onClick={() => fileRef.current?.click()} disabled={uploading}>
                <ImagePlus size={15} strokeWidth={1.8} aria-hidden="true" />
                {uploading ? "Uploading..." : form.logo_url ? "Change logo" : "Upload logo"}
              </button>
              {form.logo_url && (
                <button
                  type="button"
                  className="mb-text-button"
                  onClick={() => setForm((current) => ({ ...current, logo_upload_id: null, logo_url: null }))}
                >
                  <Trash2 size={14} strokeWidth={1.8} aria-hidden="true" /> Remove
                </button>
              )}
              <p className="ar-muted ar-small">PNG with a transparent background looks best.</p>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                onChange={(event) => uploadLogo(event.target.files?.[0])}
              />
            </div>
          </div>
          <div className="ar-grid-2">
            {FIELDS.map(([key, label, limit]) => (
              <label key={key} className="ar-field">
                <span className="ar-field-label">{label}</span>
                <input type="text" value={form[key] || ""} maxLength={limit} onChange={set(key)} />
              </label>
            ))}
          </div>
          <label className="ar-field">
            <span className="ar-field-label">Address</span>
            <input type="text" value={form.address || ""} maxLength={300} onChange={set("address")} />
          </label>
          <label className="ar-field">
            <span className="ar-field-label">Terms printed on quotations</span>
            <textarea rows={4} value={form.terms || ""} maxLength={3000} onChange={set("terms")} />
          </label>
          <div className="mb-modal-actions">
            <button type="button" className="mb-text-button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="mb-primary" disabled={busy || uploading}>
              {busy ? "Saving..." : "Save details"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
